import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lightbulb, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const SECTION_BY_PATH = [
  ["/portafoglio", "portfolio"], ["/azienda", "company"], ["/periodi", "periods"],
  ["/questionario-mef", "mef"], ["/pmi-banche", "mef"], ["/questionario", "esg"], ["/attivita", "review"], ["/incongruenze", "review"],
];
const seenKey = (u, s) => `guide-seen-${u}-${s}`;

// Suggerimento contestuale alla prima visita di una sezione (testo curato) + "Chiedimi di più" (LLM in streaming).
export function SectionHint({ userId, isConsultant }) {
  const { pathname } = useLocation();
  const section = (SECTION_BY_PATH.find(([p]) => pathname.startsWith(p)) || [])[1];
  const [hint, setHint] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setHint(null); setAnswer(""); setQuestion("");
    if (!isConsultant || !section || !userId || localStorage.getItem(seenKey(userId, section))) return;
    api.get(`/consultant/guide/${section}`).then((r) => setHint(r.data)).catch(() => setHint(null));
  }, [section, isConsultant, userId]);

  if (!hint) return null;
  const dismiss = () => { localStorage.setItem(seenKey(userId, section), "1"); setHint(null); };
  const ask = async () => {
    if (question.trim().length < 3) return;
    setBusy(true); setAnswer("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultant/guide/ask`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ section, question: question.trim() }),
      });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) { const t = p.replace(/^data: /, ""); if (t && t !== "[DONE]") { try { setAnswer((a) => a + JSON.parse(t)); } catch { /* chunk non valido */ } } }
      }
    } catch { setAnswer("Non riesco a rispondere adesso. Riprova tra poco."); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950 space-y-2" data-testid="section-hint">
      <div className="flex items-start gap-2">
        <Lightbulb className="w-5 h-5 text-sky-700 shrink-0" aria-hidden="true" />
        <div className="flex-1"><p className="font-semibold" data-testid="section-hint-title">{hint.title}</p><p data-testid="section-hint-text">{hint.text}</p></div>
        <button type="button" onClick={dismiss} aria-label="Chiudi suggerimento" data-testid="section-hint-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Chiedimi di più, es. da dove comincio?"
          className="flex-1 min-w-[200px] rounded-md border border-sky-200 px-2 py-1 text-sm bg-white" data-testid="section-hint-question" />
        <Button size="sm" variant="outline" onClick={ask} disabled={busy || question.trim().length < 3} data-testid="section-hint-ask">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : "Chiedimi di più"}
        </Button>
      </div>
      {answer && <p className="rounded-md bg-white border border-sky-100 p-2 whitespace-pre-wrap" data-testid="section-hint-answer">{answer}</p>}
    </div>
  );
}
